import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Dimensions, FlatList} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {RFValue} from 'react-native-responsive-fontsize';
import {VoucherDTO} from '../../../../dtos/VoucherDTO';
import {CouponCard} from '../CouponCard';
import {
  BuyCardIconCircle,
  BuyCardLabel,
  BuyCardWrapper,
  CardItem,
  CardsTrack,
  CarouselWrapper,
  CHEV_SIZE,
  ChevBtn,
  Dot,
  DotBtn,
  DotsRow,
  ITEM_GAP,
} from './styles';
import { useTheme } from 'styled-components/native';

const AnimatedFlatList = Animated.createAnimatedComponent(
  FlatList,
) as unknown as typeof FlatList;

const SCREEN_W = Dimensions.get('window').width;
const CARD_W_MULTI = Math.round(SCREEN_W * 0.58);
const CARD_W_SINGLE = Math.round(SCREEN_W * 0.58);
const SIDE_PAD = 24;

const BUY_SENTINEL = {_isBuyCard: true as const};
type ListItem = VoucherDTO | typeof BUY_SENTINEL;

interface Props {
  items: VoucherDTO[];
  onPressCard: (item: VoucherDTO) => void;
  onSupportPress: (item: VoucherDTO) => void;
  onRefundPress?: (item: VoucherDTO) => void;
  isBlockedFn?: (item: VoucherDTO) => boolean;
  onBuyPress?: () => void;
}

export function CouponCarousel({
  items,
  onPressCard,
  onSupportPress,
  onRefundPress,
  isBlockedFn,
  onBuyPress,
}: Props) {
  const listData = useMemo<ListItem[]>(() => [...items, BUY_SENTINEL], [items]);
  const totalCount = listData.length;

  const single = totalCount === 1;
  const CARD_W = single ? CARD_W_SINGLE : CARD_W_MULTI;
  const CARD_H = RFValue(270);
  const SNAP = CARD_W + ITEM_GAP;
  const CHEV_LEFT = 24;
  const CHEV_RIGHT = 24;
  const CHEV_TOP = (CARD_H - CHEV_SIZE) / 2;

  const scrollX = useRef(new Animated.Value(0)).current;
  const listRef = useRef<FlatList<ListItem>>(null as any);
  const [activeIndex, setActiveIndex] = useState(0);

  function handlePressCard(item: VoucherDTO, index: number) {
    if (index !== activeIndex) {
      goTo(index);
    } else {
      onPressCard(item);
    }
  }

  useEffect(() => {
    setActiveIndex(0);
    listRef.current?.scrollToOffset({offset: 0, animated: false});
  }, [items.length]);

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, totalCount - 1));
    listRef.current?.scrollToOffset({offset: clamped * SNAP, animated: true});
    setActiveIndex(clamped);
  };

  const theme = useTheme();

  return (
    <CarouselWrapper>
      <CardsTrack cardH={CARD_H}>
        <AnimatedFlatList
          ref={listRef as any}
          data={listData}
          keyExtractor={(item: any, i) =>
            '_isBuyCard' in item
              ? 'buy-card'
              : String((item as VoucherDTO).id ?? i)
          }
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEnabled={!single}
          snapToInterval={SNAP}
          decelerationRate="fast"
          contentContainerStyle={{
            paddingLeft: SIDE_PAD,
            paddingRight: SCREEN_W - SIDE_PAD - CARD_W,
          }}
          onScroll={Animated.event(
            [{nativeEvent: {contentOffset: {x: scrollX}}}],
            {useNativeDriver: true},
          )}
          scrollEventThrottle={16}
          onMomentumScrollEnd={(e: any) => {
            const idx = Math.round(e.nativeEvent.contentOffset.x / SNAP);
            setActiveIndex(Math.max(0, Math.min(idx, totalCount - 1)));
          }}
          renderItem={({item, index}: {item: any; index: number}) => {
            const inputRange = [
              (index - 1) * SNAP,
              index * SNAP,
              (index + 1) * SNAP,
            ];
            const scale = scrollX.interpolate({
              inputRange,
              outputRange: [0.91, 1, 0.91],
              extrapolate: 'clamp',
            });
            const opacity = scrollX.interpolate({
              inputRange,
              outputRange: [0.5, 1, 0.5],
              extrapolate: 'clamp',
            });

            if ('_isBuyCard' in item) {
              return (
                <CardItem
                  cardW={CARD_W}
                  cardH={CARD_H}
                  style={{transform: [{scale}], opacity}}>
                  <BuyCardWrapper
                    activeOpacity={0.85}
                    onPress={onBuyPress}
                    style={{flex: 1}}>
                    <BuyCardIconCircle>
                      <MaterialCommunityIcons
                        name="cart-outline"
                        size={RFValue(24)}
                        color={theme.colors.primary}
                      />
                    </BuyCardIconCircle>
                    <BuyCardLabel>Comprar cupom</BuyCardLabel>
                  </BuyCardWrapper>
                </CardItem>
              );
            }

            const typedItem = item as VoucherDTO;
            const blocked = isBlockedFn?.(typedItem) ?? false;
            return (
              <CardItem
                cardW={CARD_W}
                cardH={CARD_H}
                style={{transform: [{scale}], opacity}}>
                <CouponCard
                  data={typedItem}
                  action={() => handlePressCard(typedItem, index)}
                  isBlocked={blocked}
                  onSupportPress={() => onSupportPress(typedItem)}
                  onRefundPress={() => onRefundPress?.(typedItem)}
                  cardWidth={CARD_W}
                />
              </CardItem>
            );
          }}
        />

        {activeIndex > 0 && (
          <ChevBtn
            side="left"
            pos={CHEV_LEFT}
            top={CHEV_TOP}
            activeOpacity={0.75}
            onPress={() => goTo(activeIndex - 1)}>
            <MaterialCommunityIcons
              name="chevron-left"
              size={RFValue(22)}
              color="#fff"
            />
          </ChevBtn>
        )}

        {activeIndex < totalCount - 1 && (
          <ChevBtn
            side="right"
            pos={CHEV_RIGHT}
            top={CHEV_TOP}
            activeOpacity={0.75}
            onPress={() => goTo(activeIndex + 1)}>
            <MaterialCommunityIcons
              name="chevron-right"
              size={RFValue(22)}
              color="#fff"
            />
          </ChevBtn>
        )}
      </CardsTrack>

      {totalCount > 1 && (
        <DotsRow>
          {listData.map((_, i) => (
            <DotBtn key={i} onPress={() => goTo(i)} activeOpacity={0.8}>
              <Dot active={activeIndex === i} />
            </DotBtn>
          ))}
        </DotsRow>
      )}
    </CarouselWrapper>
  );
}
